import { useEffect, useState } from "react";
import { CaretDown, CaretUp, ClipboardText, X, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

export default function TrainingIncompleteCard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState("");
  const [signing, setSigning] = useState(null);
  const [date, setDate] = useState("");
  const [method, setMethod] = useState("paper_on_file");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/training/incomplete-by-topic").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  if (!data) return null;

  const openSignoff = (topic, person) => {
    setSigning({ topic_id: topic.topic_id, topic_title: topic.title, ...person });
    setDate("");
    setMethod("paper_on_file");
    setNote("");
  };

  const submit = async () => {
    if (!date) { toast.error("Enter the completion date from their record"); return; }
    setBusy(true);
    try {
      await api.post(`/training/topics/${signing.topic_id}/sign-off`, {
        user_id: signing.user_id, completed_at: date, method, note,
      });
      toast.success(`Signed off: ${signing.name} — ${signing.topic_title}`);
      setSigning(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm md:col-span-2" data-testid="training-incomplete-card">
      <div className="flex items-center gap-2">
        <ClipboardText size={16} className="text-primary" />
        <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
          Training incomplete, by training <span className={data.total ? "text-[#C0392B]" : "text-[#1e8449]"}>({data.total})</span>
        </p>
      </div>
      <p className="mt-1 text-xs text-stone-400">Tap a person to open the sign-off — name and training are pre-filled, just add the date from their record.</p>
      <div className="mt-3 grid max-h-80 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
        {(data.topics || []).map((t) => (
          <div key={t.topic_id} className="rounded-lg border border-stone-200" data-testid={`incomplete-topic-${t.topic_id}`}>
            <button type="button" onClick={() => setOpen(open === t.topic_id ? "" : t.topic_id)}
              className="flex w-full items-center justify-between px-3 py-2 text-left" data-testid={`incomplete-topic-toggle-${t.topic_id}`}>
              <p className="text-sm font-medium text-stone-800">{t.title}</p>
              <span className="flex items-center gap-1.5">
                {t.never_count > 0 && <span className="rounded-full bg-[#C0392B]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#C0392B]" title="Never completed on record">{t.never_count} never</span>}
                {t.lapsed_count > 0 && <span className="rounded-full bg-[#B7791F]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#8a5d14]" title="Completed before, now overdue">{t.lapsed_count} lapsed</span>}
                <span className="rounded-full bg-[#C0392B]/10 px-2 py-0.5 text-xs font-bold text-[#C0392B]">{t.count}</span>
                {open === t.topic_id ? <CaretUp size={14} className="text-stone-400" /> : <CaretDown size={14} className="text-stone-400" />}
              </span>
            </button>
            {open === t.topic_id && (
              <ul className="max-h-40 space-y-1 overflow-y-auto border-t border-stone-100 px-3 py-2">
                {t.people.map((p) => (
                  <li key={p.user_id}>
                    <button type="button" onClick={() => openSignoff(t, p)}
                      className="flex w-full items-baseline justify-between gap-2 rounded px-1 py-0.5 text-left hover:bg-accent"
                      data-testid={`incomplete-person-${t.topic_id}-${p.user_id}`}>
                      <span className="text-sm text-primary underline-offset-2 hover:underline">{p.name}</span>
                      {p.history === "never" ? (
                        <span className="shrink-0 rounded-full bg-[#C0392B]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#C0392B]" title={p.kind === "never" ? "Not started yet — first due at their 1-year mark" : "Never completed on record"} data-testid={`incomplete-never-${t.topic_id}-${p.user_id}`}>
                          never done{p.kind === "never" && p.due ? ` · due ${p.due}` : ""}
                        </span>
                      ) : (
                        <span className="shrink-0 rounded-full bg-[#B7791F]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#8a5d14]" title="Completed before, but the renewal date has passed" data-testid={`incomplete-lapsed-${t.topic_id}-${p.user_id}`}>
                          lapsed · last {p.last_done}{p.due ? ` · due ${p.due}` : ""}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
        {!(data.topics || []).length && <p className="text-xs font-medium text-[#1e8449]">Everyone current on every training.</p>}
      </div>

      {signing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="signoff-modal">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between">
              <p className="font-heading text-lg font-semibold text-stone-900">Record sign-off</p>
              <button onClick={() => setSigning(null)} className="text-stone-400 hover:text-stone-700" data-testid="signoff-close"><X size={18} /></button>
            </div>
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Employee</Label>
                  <Input value={signing.name} readOnly className="border-stone-200 bg-stone-50" data-testid="signoff-name" />
                </div>
                <div className="space-y-1">
                  <Label>Training</Label>
                  <Input value={signing.topic_title} readOnly className="border-stone-200 bg-stone-50" data-testid="signoff-topic" />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Completion date (from their record)</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border-stone-300" data-testid="signoff-date" />
              </div>
              <div className="space-y-1">
                <Label>How it was completed</Label>
                <select value={method} onChange={(e) => setMethod(e.target.value)}
                  className="h-10 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="signoff-method">
                  <option value="paper_on_file">Paper sign-off on file in the office</option>
                  <option value="interactive">Live interactive session (in person)</option>
                  <option value="online">Completed online</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label>Note (optional)</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. certificate in 2024 training binder" className="border-stone-300" data-testid="signoff-note" />
              </div>
              <Button onClick={submit} disabled={busy} className="w-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="signoff-submit">
                {busy ? "Saving…" : "Record sign-off"}
              </Button>
              <p className="text-[11px] text-stone-400">This records the completion with today's audit trail and the date you enter. It never changes the original completion date.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
